import {
  FETCH_ATTRIBUTION_OPTIONS,
  FETCH_ATTRIBUTION_OPTIONS_START,
  FETCH_ATTRIBUTION_OPTIONS_ERROR,
  CREATE_ATTRIBUTION_OPTION,
  UPDATE_ATTRIBUTION_OPTION,
  DELETE_ATTRIBUTION_OPTION,
} from '../constants/ActionTypes';
// Read OS enum from constants/ to break the circular dep
// (actions → store → reducers) that Jest's module init can't resolve.
import { OS_IOS, OS_ANDROID, OS_WEB_APP } from '../constants/attributionOs';

const INITIAL_STATE = {
  iOS: [],
  android: [],
  webApp: [],
  loading: false,
  error: null,
};

// The API response keys options by `os_label` (e.g. "iOS", "Android", "Web App").
// A single option row has `{ id, value, os, os_label, created_at, updated_at }`.
function normalizeFromApi(payload) {
  // Accept both label-keyed shape and possible raw arrays.
  if (Array.isArray(payload)) {
    return {
      iOS: payload.filter(o => o.os === OS_IOS),
      android: payload.filter(o => o.os === OS_ANDROID),
      webApp: payload.filter(o => o.os === OS_WEB_APP),
    };
  }
  return {
    iOS: payload?.iOS || payload?.ios || [],
    android: payload?.Android || payload?.android || [],
    webApp: payload?.['Web App'] || payload?.webApp || payload?.web_app || [],
  };
}

function bucketForOs(os) {
  if (os === OS_IOS) return 'iOS';
  if (os === OS_ANDROID) return 'android';
  if (os === OS_WEB_APP) return 'webApp';
  return null;
}

export default function (state = INITIAL_STATE, action) {
  switch (action.type) {
    case FETCH_ATTRIBUTION_OPTIONS_START:
      return { ...state, loading: true, error: null };

    case FETCH_ATTRIBUTION_OPTIONS: {
      const normalized = normalizeFromApi(action.payload);
      return {
        ...state,
        iOS: normalized.iOS,
        android: normalized.android,
        webApp: normalized.webApp,
        loading: false,
        error: null,
      };
    }

    case FETCH_ATTRIBUTION_OPTIONS_ERROR:
      return { ...state, loading: false, error: action.payload || 'fetch_error' };

    case CREATE_ATTRIBUTION_OPTION: {
      const bucket = bucketForOs(action.payload?.os);
      if (!bucket) return state;
      return { ...state, [bucket]: [...state[bucket], action.payload] };
    }

    case UPDATE_ATTRIBUTION_OPTION: {
      const updated = action.payload;
      if (!updated?.id) return state;
      // Remove the record from all buckets (OS may have changed), then re-insert.
      const iOS = state.iOS.filter(o => o.id !== updated.id);
      const android = state.android.filter(o => o.id !== updated.id);
      const webApp = state.webApp.filter(o => o.id !== updated.id);
      const bucket = bucketForOs(updated.os);
      if (bucket === 'iOS') {
        return { ...state, iOS: [...iOS, updated], android, webApp };
      }
      if (bucket === 'android') {
        return { ...state, iOS, android: [...android, updated], webApp };
      }
      if (bucket === 'webApp') {
        return { ...state, iOS, android, webApp: [...webApp, updated] };
      }
      return { ...state, iOS, android, webApp };
    }

    case DELETE_ATTRIBUTION_OPTION: {
      const { id, os } = action.payload || {};
      const bucket = bucketForOs(os);
      if (bucket) {
        return { ...state, [bucket]: state[bucket].filter(o => o.id !== id) };
      }
      // Fallback: drop from all.
      return {
        ...state,
        iOS: state.iOS.filter(o => o.id !== id),
        android: state.android.filter(o => o.id !== id),
        webApp: state.webApp.filter(o => o.id !== id),
      };
    }

    default:
      return state;
  }
}
